'use client';

import { useEffect, useState } from 'react';
import { SidebarLeft } from '@/components/sidebar/sidebar-left';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { MaintenanceAlert } from '@/components/maintenance-alert';
import { Loader2 } from 'lucide-react';
import { checkApiHealth } from '@/services/common';
import MaintenancePage from '@/components/deep-mining/maintenance-page';
import { DeleteOperationProvider } from '@/contexts/DeleteOperationContext';
import { StatusOverlay } from '@/components/ui/status-overlay';
import { MemoryLibrary } from '@/components/memory-library/memory-library';
import { PointsDetailsModal } from '@/components/deep-mining/points-details-modal';
import { AccessKeyModal } from '@/components/sidebar/access-key-modal';
import { UserInfoProvider } from '@/contexts/user-info-context';
import { SpacePointsProvider } from '@/contexts/space-points-context';
import { useStores } from '@/store/hooks/useStore';
import { SmartWatermark } from '@/components/deep-mining/with-watermark';
import UpdateVersionModal from '@/components/update-version-modal';
import useAppNotice from '@/hooks/use-app-notice';
import RegionBanModal from '@/components/region-ban-modal';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const AppNoticeContainer = () => {
  useAppNotice();
  return null;
};

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [showMaintenanceAlert, setShowMaintenanceAlert] = useState(false);
  const [isApiHealthy, setIsApiHealthy] = useState(true);
  const [isCheckingHealth, setIsCheckingHealth] = useState(true);
  const stores = useStores();

  useEffect(() => {
    setShowMaintenanceAlert(false);
  }, []);

  // Check API health
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await checkApiHealth();
        setIsApiHealthy(health.status === 'ok');
      } catch (error) {
        console.error('API health check failed:', error);
        setIsApiHealthy(false);
      } finally {
        setIsCheckingHealth(false);
      }
    };

    checkHealth();
    // Check health every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  // Show loading state while checking auth or health
  if (stores.userStore.isUseInfoLoading || isCheckingHealth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Don't render anything if not authenticated
  if (!stores.userStore.userInfo) {
    return null;
  }
  // Show maintenance page if API is not healthy
  if (!isApiHealthy) {
    return <MaintenancePage />;
  }

  // 主内容渲染
  return (
    <UserInfoProvider>
      <DashboardContent
        showMaintenanceAlert={showMaintenanceAlert}
        setShowMaintenanceAlert={setShowMaintenanceAlert}
      >
        {children}
        <AppNoticeContainer />
        <UpdateVersionModal />
        {/* <RegionBanModal /> */}
      </DashboardContent>
    </UserInfoProvider>
  );
}

// 全局内容与水印整合组件
function DashboardContent({
  children,
  showMaintenanceAlert,
  setShowMaintenanceAlert,
}: {
  children: React.ReactNode;
  showMaintenanceAlert: boolean;
  setShowMaintenanceAlert: (show: boolean) => void;
}) {
  return (
    <SmartWatermark>
      <DeleteOperationProvider>
        <SpacePointsProvider>
          <SidebarProvider>
            <SidebarLeft />
            <SidebarInset>
              <div className="bg-background">{children}</div>
            </SidebarInset>
            <MaintenanceAlert
              open={showMaintenanceAlert}
              onOpenChange={setShowMaintenanceAlert}
              closeable={true}
            />
            {/* Status overlay for deletion operations */}
            <StatusOverlay />
            {/* 记忆库管理组件 */}
            <MemoryLibrary />
            {/* 积分使用明细弹窗 */}
            <PointsDetailsModal />
            {/* Access Key 管理弹窗 */}
            <AccessKeyModal />
          </SidebarProvider>
        </SpacePointsProvider>
      </DeleteOperationProvider>
    </SmartWatermark>
  );
}
